import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _get from "lodash/get";
import "core-js/modules/es6.object.to-string.js";
import "core-js/modules/es6.promise.js";
import { v4 as uuidv4 } from 'uuid';
import { APPLE_SIGNIN_POST_MESSAGE_SOURCE } from "../../constants";
import { AppleAuthErrors } from "../../constants/internal";
import { openPopup } from "./popup";
/**
 * Opens a new window which shows the Sign in with Apple authorization flow
 * at https://appleid.apple.com/auth/authorize.
 *
 * @returns {Promise} A promise tracking the process:
 *  - resolves with the Apple auth response (authorization code, user info)
 *  - rejects with {string} error: one of `AppleAuthErrors` or the error from Apple
 */

var showApplePopup = function showApplePopup() {
  return new Promise(function (resolve, reject) {
    var state = uuidv4();
    var popup = openPopup(state);
    var windowCloseInterval;
    var messageHandler; // on either success or fail make sure to clean up any polling, event listeners, etc

    var makeCleanupHandler = function makeCleanupHandler(cb) {
      return function () {
        window.clearInterval(windowCloseInterval);
        window.removeEventListener('message', messageHandler);
        if (popup && !popup.closed && popup.close) popup.close();
        cb.apply(void 0, arguments);
      };
    };

    var handleResolve = makeCleanupHandler(resolve);
    var handleReject = makeCleanupHandler(reject); // while window is open poll to check if it's been closed by the user

    windowCloseInterval = window.setInterval(function () {
      if (!popup || popup && popup.closed) {
        handleReject(AppleAuthErrors.POPUP_CLOSED_BY_USER);
      }
    }, 200); // handler for `postMessage` from popup-->parent window

    messageHandler = function messageHandler(message) {
      // ignore messages not from redirect handler page
      if (message.data.source !== APPLE_SIGNIN_POST_MESSAGE_SOURCE) return; // only allow messages from the same domain

      if (message.origin !== window.location.origin) {
        handleReject(AppleAuthErrors.CROSS_ORIGIN_POST_MESSAGE); // if the apple flow returns an `error` field reject with the error
        // https://developer.apple.com/documentation/signinwithapplejs/configuring_your_webpage_for_sign_in_with_apple
      } else if (_get(message, 'data.responseData.error')) {
        handleReject(message.data.responseData.error); // verify the state param to prevent XSRF
      } else if (_get(message, 'data.responseData.state') !== state) {
        handleReject(AppleAuthErrors.STATE_PARAM_MISMATCH);
      } else {
        handleResolve(_objectSpread(_objectSpread({}, message.data.responseData), {}, {
          redirect_uri: "".concat(window.location.origin, "/home/apple-signin/redirect-handler.html")
        }));
      }
    };

    window.addEventListener('message', messageHandler);
  });
};
/**
 * If the user was required to enter a password ("REQUIRES_PASSWORD" error), we can
 * avoid showing Apple Sign-in auth again by passing appropriate params to Identity.
 *
 * @param {string} email
 * @param {string} code
 *
 * @return {Promise}
 */


var bypassAppleAuth = function bypassAppleAuth(email, code) {
  return new Promise(function (resolve) {
    return resolve({
      user: {
        email: email
      },
      code: code
    });
  });
};

var getWebAppleTokenInfo = function getWebAppleTokenInfo() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      usingPassword = _ref.usingPassword,
      email = _ref.email,
      code = _ref.code;

  var shouldBypassPopup = usingPassword && email && code;
  return shouldBypassPopup ? bypassAppleAuth(email, code) : showApplePopup();
};

export default getWebAppleTokenInfo;